import { Board } from '../types';

/**
 * Export a board to a JSON file and trigger download
 */
export const exportBoardToJSON = (board: Board): void => {
    const exportData = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        board: {
            ...board,
            // Reset view state for import
            viewState: undefined,
        },
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${board.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_export.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * Import a board from a JSON file
 */
export const importBoardFromJSON = (file: File): Promise<Board> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const content = event.target?.result as string;
                const data = JSON.parse(content);

                // Validate the import data structure
                if (!data.board || !data.board.notes) {
                    throw new Error('Invalid board export file');
                }

                // Create a new board with a new ID and timestamp
                const importedBoard: Board = {
                    ...data.board,
                    id: Math.random().toString(36).substr(2, 9),
                    title: `${data.board.title} (Imported)`,
                    lastEdited: new Date().toLocaleString(),
                    isFavorite: false,
                    isDeleted: false,
                };

                resolve(importedBoard);
            } catch {
                reject(new Error('Failed to parse board file. Please ensure it is a valid NotesScape export.'));
            }
        };

        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };

        reader.readAsText(file);
    });
};

/**
 * Create a file input and trigger file selection
 */
export const selectFileForImport = (): Promise<File> => {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';

        input.onchange = (event) => {
            const file = (event.target as HTMLInputElement).files?.[0];
            if (file) {
                resolve(file);
            } else {
                reject(new Error('No file selected'));
            }
        };

        input.click();
    });
};
